/**
 * Mobile Category Details Hook
 * Extracted from CreateTransactionMobile.tsx
 * 
 * Handles category detail management for mobile transaction form
 */

import { useState, useCallback, useEffect } from "react";
import { TransactionCategory, TransactionCategoryDetail, TransactionCategoryGroup, TransactionDetailValue, Transaction } from "../../../../types";

export interface UseMobileCategoryDetailsParams {
  transactionType: "expense" | "income" | "transfer";
  isEditMode: boolean;
  formData: {
    category: string;
    details?: TransactionDetailValue;
  };
  expenseCategories: TransactionCategory[];
  incomeCategories: TransactionCategory[];
  categoryGroups: TransactionCategoryGroup[];
  transactions: Transaction[];
  setSelectedCategoryDetails: (details: TransactionCategoryDetail[]) => void;
  setFormData: (updater: (prev: any) => any) => void;
}

export interface UseMobileCategoryDetailsResult {
  selectedCategoryDetails: TransactionCategoryDetail[];
  setSelectedCategoryDetails: (details: TransactionCategoryDetail[]) => void;
  deriveCategoryDetails: (categoryName: string, preserveExistingValues: boolean) => void;
  handleCategorySelect: (categoryName: string) => void;
}

/**
 * Hook for managing category details in mobile transaction form
 */
export const useMobileCategoryDetails = ({
  transactionType,
  isEditMode,
  formData,
  expenseCategories,
  incomeCategories,
  categoryGroups,
  transactions,
  setSelectedCategoryDetails,
  setFormData,
}: UseMobileCategoryDetailsParams): UseMobileCategoryDetailsResult => {
  const [selectedCategoryDetails, setSelectedCategoryDetailsState] = useState<TransactionCategoryDetail[]>([]);

  // Helper function to derive category details from selected category
  const deriveCategoryDetails = useCallback(
    (categoryName: string, preserveExistingValues: boolean = false) => {
      try {
        const categories = transactionType === "expense" ? expenseCategories : incomeCategories;
        const selectedCategory = categories.find((cat) => cat.name === categoryName);

        if (!selectedCategory) {
          setSelectedCategoryDetailsState([]);
          return;
        }

        // Get details from the category's group
        const group = selectedCategory?.groupId ? categoryGroups.find((g) => g.id === selectedCategory.groupId) : null;
        const allGroupDetails = group?.details || [];

        // Only show details that are explicitly enabled for this category
        const enabledDetailNames = Array.isArray(selectedCategory?.enabledDetails) ? selectedCategory.enabledDetails : [];

        const details = enabledDetailNames.length > 0 ? allGroupDetails.filter((detail) => enabledDetailNames.includes(detail.name)) : [];

        setSelectedCategoryDetailsState(details);
        setSelectedCategoryDetails(details);

        // Initialize details with default values from category settings or previous transactions
        let detailsValue: TransactionDetailValue | undefined = undefined;
        if (details.length > 0) {
          detailsValue = preserveExistingValues && formData.details ? { ...formData.details } : {};

          // Use default values from category settings
          // IMPORTANT: Always use the latest category data from the categories array (updated by real-time listeners)
          const categoryDefaults =
            selectedCategory?.defaultValues && typeof selectedCategory.defaultValues === "object" && !Array.isArray(selectedCategory.defaultValues)
              ? selectedCategory.defaultValues
              : {};

          for (const detail of details) {
            // If preserving existing values and this detail already has a value, keep it
            if (preserveExistingValues && formData.details && detail.name in formData.details) {
              detailsValue[detail.name] = formData.details[detail.name];
              continue;
            }

            const hasDefault = detail.name in categoryDefaults;
            const defaultVal = hasDefault ? categoryDefaults[detail.name] : undefined;

            // Priority 1: Use default value from category settings
            if (hasDefault && defaultVal !== "" && defaultVal !== null && defaultVal !== undefined) {
              if (detail.type === "list" && detail.listItems && Array.isArray(detail.listItems)) {
                const defaultValStr = String(defaultVal);
                // Always apply the default value, even if it's not in the list items
                // The Autocomplete component can handle values not in the list (freeSolo mode)
                detailsValue[detail.name] = defaultValStr;
              } else if (detail.type === "measurable") {
                const numVal = typeof defaultVal === "number" ? defaultVal : typeof defaultVal === "string" ? parseFloat(defaultVal) : 0;
                if (!isNaN(numVal)) {
                  detailsValue[detail.name] = numVal;
                } else {
                  detailsValue[detail.name] = "";
                }
              } else {
                detailsValue[detail.name] = String(defaultVal);
              }
            } else if (detail.type === "measurable") {
              // For measurable, try to get last value from previous transactions
              const previousTransactions = transactions.filter((t) => t.category === categoryName && t.details && detail.name in t.details);
              if (previousTransactions.length > 0 && previousTransactions[0].details) {
                detailsValue[detail.name] = previousTransactions[0].details[detail.name];
              } else {
                detailsValue[detail.name] = "";
              }
            } else if (detail.type === "list") {
              detailsValue[detail.name] = "";
            } else {
              detailsValue[detail.name] = "";
            }
          }
        } else {
          detailsValue = undefined;
        }

        // Update form data with new details (preserve existing values if requested)
        // Use functional update to avoid dependency on formData.details
        // Always update if preserveExistingValues is false (manual category selection)
        // Or if form has no details yet (new transaction)
        if (!preserveExistingValues || !formData.details || Object.keys(formData.details || {}).length === 0) {
          setFormData((prev) => {
            // Only update if details actually changed to prevent infinite loops
            const currentDetails = prev.details;
            const detailsChanged = JSON.stringify(currentDetails) !== JSON.stringify(detailsValue);
            if (!detailsChanged && currentDetails !== undefined) {
              return prev; // No change, return same object
            }
            return {
              ...prev,
              details: detailsValue,
            };
          });
        }
      } catch (error) {
        console.error("❌ ERROR in deriveCategoryDetails:", error);
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [transactionType, expenseCategories, incomeCategories, categoryGroups, transactions, setFormData, setSelectedCategoryDetails] // Removed formData.details to prevent infinite loop
  );

  // Sync category details when categories, groups, or selected category changes (create mode only)
  // In edit mode, details are set from the transaction in useMobileEditMode; do not overwrite with settings defaults
  useEffect(() => {
    if (isEditMode) return;
    // Only sync if a category is already selected
    if (formData.category && formData.category.trim() !== "" && transactionType !== "transfer") {
      // Check if form is empty (new transaction) - if so, don't preserve values, apply defaults
      const isFormEmpty = !formData.details || Object.keys(formData.details).length === 0;
      
      // Find the selected category to check if default values changed
      const categories = transactionType === "expense" ? expenseCategories : incomeCategories;
      const selectedCategory = categories.find((cat) => cat.name === formData.category);
      
      // Check if default values have changed by comparing current category defaults with form values
      let defaultsChanged = false;
      let shouldApplyNewDefaults = false;
      if (selectedCategory) {
        const categoryDefaults = selectedCategory?.defaultValues || {};
        const formDetails = formData.details || {};
        
        // Check if any default value in category doesn't match form value
        // When defaults are updated in settings, we want to apply them automatically
        for (const [key, value] of Object.entries(categoryDefaults)) {
          if (value !== "" && value !== null && value !== undefined) {
            const formValue = formDetails[key];
            const formValueExists = key in formDetails && formValue !== undefined && formValue !== null && formValue !== "";
            
            // If form value doesn't match the new default, defaults have changed
            if (!formValueExists || formValue !== value) {
              defaultsChanged = true;
              // Always apply new defaults if:
              // 1. Form is empty (new transaction)
              // 2. Form value is empty/missing (wasn't set)
              // 3. Form value doesn't match new default (default was changed in settings)
              // This ensures that when defaults are edited in settings, they automatically update in the form
              if (isFormEmpty || !formValueExists || formValue !== value) {
                shouldApplyNewDefaults = true;
              }
              // Don't break - check all defaults to see if any should be applied
            }
          }
        }
        
        // Also check if form has values that aren't in current defaults (category was updated)
        // This helps detect when defaults were removed or changed
        if (!defaultsChanged && !isFormEmpty && formDetails) {
          for (const key of Object.keys(formDetails)) {
            if (!(key in categoryDefaults) || categoryDefaults[key] === "" || categoryDefaults[key] === null || categoryDefaults[key] === undefined) {
              // Form has a value but category no longer has a default for it
              defaultsChanged = true;
              // If the form value is empty, we should apply new defaults
              if (formDetails[key] === "" || formDetails[key] === null || formDetails[key] === undefined) {
                shouldApplyNewDefaults = true;
              }
              break;
            }
          }
        }
        
      }
      
      // When defaults changed, always apply new defaults (don't preserve old values)
      // This ensures that when defaults are edited in settings, they automatically update in the transaction form
      // For new transactions (empty form), always apply defaults
      // For existing transactions, if defaults changed, apply new defaults (user can change them back if needed)
      const shouldPreserve = !isFormEmpty && !defaultsChanged;
      deriveCategoryDetails(formData.category, shouldPreserve);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [isEditMode, categoryGroups, expenseCategories, incomeCategories, formData.category, transactionType]); // Removed deriveCategoryDetails to prevent infinite loop

  // Handle category selection
  const handleCategorySelect = useCallback(
    (categoryName: string) => {
      try {
        // Set the category and clear details to ensure defaults are applied
        setFormData((prev) => ({
          ...prev,
          category: categoryName,
          details: undefined, // Clear details so defaults are always applied
        }));

        // Derive category details immediately (don't preserve existing values on manual selection)
        // Always use false to apply defaults when selecting a category
        deriveCategoryDetails(categoryName, false);
      } catch (error) {
        console.error("❌ ERROR in handleCategorySelect:", error);
        // Still set the category even if there's an error
        setFormData((prev) => ({
          ...prev,
          category: categoryName,
        }));
      }
    },
    [setFormData, deriveCategoryDetails]
  );

  return {
    selectedCategoryDetails,
    setSelectedCategoryDetails: setSelectedCategoryDetailsState,
    deriveCategoryDetails,
    handleCategorySelect,
  };
};
